import { useMemo } from 'react';
import {
  useActivityFilter,
  useGetInsuranceTypes,
} from 'infrastructure/hooks/rpm';
import { endOfDay, startOfDay } from 'date-fns';
import { timePresetKeys } from 'infrastructure/enums';

import type { ParentClinicOption } from 'infrastructure/enums/parent-clinic';
import type { TDashboardActivityFilter } from 'infrastructure/hooks/rpm';
import type { PresetKeyEnum } from 'infrastructure/enums';

export type TDashboardFilter = ReturnType<typeof useDashboardFilter>;

export const useDashboardFilter = () => {
  const { filter, count, updateQueryParams, resetFilter } = useActivityFilter();

  const { insuranceTypes, refetchInsuranceType } = useGetInsuranceTypes(
    Boolean(filter.clinicGuid),
  );

  const dateValues: [Date, Date] | undefined = useMemo(
    () =>
      filter.startTime && filter.endTime
        ? [new Date(filter.startTime), new Date(filter.endTime)]
        : undefined,
    [filter.startTime, filter.endTime],
  );

  const updateQueryParamsAndResetPage = (
    newFilter: Partial<TDashboardActivityFilter>,
  ) => {
    updateQueryParams({
      ...newFilter,
      page: 1,
    });
  };

  const onDateChange = async (
    dates: [Date | null, Date | null],
    dateString: [string, string],
    info: { range?: 'start' | 'end' },
    presetKey?: string,
  ) => {
    const [startDate, endDate] = dates;

    if (startDate === null && endDate === null) {
      updateQueryParamsAndResetPage({
        startTime: undefined,
        endTime: undefined,
      });
      return;
    }

    const isTimePreset =
      presetKey && timePresetKeys.includes(presetKey as PresetKeyEnum);

    if (startDate && endDate) {
      updateQueryParamsAndResetPage({
        startTime: (isTimePreset ? startDate : startOfDay(startDate)).getTime(),
        endTime: (isTimePreset ? endDate : endOfDay(endDate)).getTime(),
      });
    } else {
      if (startDate && info.range === 'start') {
        updateQueryParamsAndResetPage({
          startTime: (isTimePreset
            ? startDate
            : startOfDay(startDate)
          ).getTime(),
        });
      }
      if (endDate && info.range === 'end') {
        updateQueryParamsAndResetPage({
          endTime: (isTimePreset ? endDate : endOfDay(endDate)).getTime(),
        });
      }
    }
  };

  const handleChangeValue = async (key: keyof typeof filter, value: string) => {
    if (key === 'clinicGuid') {
      updateQueryParamsAndResetPage({
        insuranceType: undefined,
        doctorGuid: undefined,
        patientGuid: undefined,
        clinicGuid: value,
      });
      refetchInsuranceType();

      return;
    }

    if (key === 'parentClinicOption') {
      updateQueryParamsAndResetPage({
        parentClinicOption: value as ParentClinicOption,
        clinicGuid: undefined,
      });
      refetchInsuranceType();

      return;
    }

    if (key === 'reviewed') {
      updateQueryParamsAndResetPage({ reviewed: Boolean(value) });

      return;
    }

    if (key === 'doctorGuid') {
      updateQueryParamsAndResetPage({
        patientGuid: undefined,
      });
    }

    updateQueryParamsAndResetPage({ [key]: value });
  };

  const onResetFilter = async () => {
    resetFilter();
  };

  return {
    onResetFilter,
    values: filter,
    dateValues,
    count,
    insuranceTypes,
    onDateChange,
    handleChangeValue,
  };
};
